import type { CalendarEvent } from '@/lib/absences';
import { parseDateOnly } from '@/lib/date';
import { format, isWithinInterval, startOfDay } from 'date-fns';
import { useMemo } from 'react';

interface ScheduleSummaryProps {
  events: CalendarEvent[];
  totalEmployees: number;
}

interface SummaryStat {
  label: string;
  count: number;
  color: string;
}

export function ScheduleSummary({ events, totalEmployees }: ScheduleSummaryProps) {
  const today = startOfDay(new Date());

  const stats = useMemo((): SummaryStat[] => {
    const todayEvents = events.filter((e) =>
      isWithinInterval(today, {
        start: startOfDay(parseDateOnly(e.startsOn)),
        end: startOfDay(parseDateOnly(e.endsOn)),
      })
    );

    // HW-701: only approved leave counts as actually away; pending is shown on
    // the calendar but excluded from the availability stat.
    const leaveCount = todayEvents.filter(
      (e) => e.type === 'leave_request' && e.status !== 'pending'
    ).length;
    const birthdayCount = todayEvents.filter((e) => e.type === 'birthday').length;
    const inOffice = Math.max(0, totalEmployees - leaveCount);

    return [
      { label: 'in office', count: inOffice, color: '#22c55e' },
      { label: 'on leave', count: leaveCount, color: '#3b82f6' },
      { label: 'birthday', count: birthdayCount, color: '#ec4899' },
    ];
  }, [events, totalEmployees, today]);

  return (
    <div className="flex items-center gap-6 border-b border-border bg-gradient-to-r from-emerald-500/[0.03] to-transparent px-6 py-2.5">
      <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
        Today, {format(today, 'MMMM d')}
      </div>
      <div className="flex items-center gap-5">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1"
          >
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: stat.color }}
            />
            <span className="text-base font-bold" style={{ color: stat.color }}>
              {stat.count}
            </span>
            <span className="text-xs text-muted-foreground">{stat.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
